import React from "react";
import "../styles/skeletons.css";

const CAROUSEL_PLACEHOLDERS = 7;

export function HomeHeroSkeleton() {
  return (
    <div
      className="home-hero-skeleton slider"
      role="status"
      aria-live="polite"
      aria-label="Loading hero banner"
    >
      <div className="home-skeleton home-hero-skeleton__banner" />
    </div>
  );
}

export function ProductCarouselSkeleton({ title, variant = "bestsellers" }) {
  const isRec = variant === "recommendations";
  const sectionClass = isRec
    ? "recommendations-section"
    : "bestsellers-section";
  const headClass = isRec
    ? "recommendations-title-and-chevron"
    : "bestsellers-title-and-chevron";
  const gridClass = isRec ? "recommendations-grid" : "bestsellers-grid";
  const itemClass = isRec
    ? "recommendations-product-item"
    : "bestsellers-product-item";

  return (
    <div
      className={sectionClass}
      role="status"
      aria-live="polite"
      aria-label={`Loading ${title}`}
    >
      <div className={headClass}>
        <div className="home-skeleton home-skeleton-title-bar" />
        <div
          className={
            isRec ? "recommendations-chevrons" : "bestsellers-chevrons"
          }
        >
          <div className="home-skeleton home-skeleton-nav-btn" />
          <div className="home-skeleton home-skeleton-nav-btn" />
        </div>
      </div>
      <div className={gridClass}>
        {Array.from({ length: CAROUSEL_PLACEHOLDERS }).map((_, i) => (
          <div key={i} className={itemClass}>
            <div className="home-skeleton-product-card">
              <div className="home-skeleton home-skeleton-product-card__image" />
              <div className="home-skeleton home-skeleton-product-card__line home-skeleton-product-card__line--lg" />
              <div className="home-skeleton home-skeleton-product-card__line home-skeleton-product-card__line--sm" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function FeaturedBrandsSkeleton() {
  return (
    <div
      className="featured-brands home-brands-skeleton"
      role="status"
      aria-live="polite"
      aria-label="Loading featured brands"
    >
      <div className="home-skeleton home-brands-skeleton__title" />
      <div className="home-skeleton home-brands-skeleton__sub" />
      <div className="home-brands-skeleton__carousel">
        <div className="home-skeleton home-brands-skeleton__nav" />
        <div className="home-brands-skeleton__viewport">
          <div className="home-brands-skeleton__track">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="home-brands-skeleton__item">
                <div className="home-skeleton home-brands-skeleton__brand-img" />
                <div className="home-skeleton home-brands-skeleton__brand-name" />
              </div>
            ))}
          </div>
        </div>
        <div className="home-skeleton home-brands-skeleton__nav" />
      </div>
    </div>
  );
}

function GridProductCardSkeleton() {
  return (
    <div className="home-skeleton-product-card">
      <div className="home-skeleton home-skeleton-product-card__image" />
      <div className="home-skeleton home-skeleton-product-card__line home-skeleton-product-card__line--lg" />
      <div className="home-skeleton home-skeleton-product-card__line home-skeleton-product-card__line--sm" />
    </div>
  );
}

export function ProductGridSkeleton({
  count = 8,
  gridClassName = "products-grid",
  itemClassName,
  ariaLabel = "Loading products",
}) {
  return (
    <div
      className={gridClassName}
      role="status"
      aria-live="polite"
      aria-label={ariaLabel}
    >
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className={itemClassName || undefined}>
          <GridProductCardSkeleton />
        </div>
      ))}
    </div>
  );
}

export function SimpleProductPageSkeleton({
  gridClassName = "products-grid",
  cardCount = 12,
  wrapperClassName = "products-page new-products-container",
}) {
  return (
    <div
      className={wrapperClassName}
      role="status"
      aria-live="polite"
      aria-label="Loading products"
    >
      <div className="home-skeleton page-skeleton-title" />
      <div className="home-skeleton page-skeleton-desc" />
      <ProductGridSkeleton count={cardCount} gridClassName={gridClassName} />
    </div>
  );
}

export function ProductDetailSkeleton() {
  return (
    <div
      className="product-view-container"
      role="status"
      aria-live="polite"
      aria-label="Loading product"
    >
      <div className="product-view">
        <div className="product-details-wrapper product-detail-skeleton">
          <div className="product-image-section">
            <div className="product-detail-skeleton__gallery">
              <div className="home-skeleton product-detail-skeleton__main" />
              <div className="product-detail-skeleton__thumbs">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    className="home-skeleton product-detail-skeleton__thumb"
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="product-details-section product-detail-skeleton__details">
            <div className="home-skeleton product-detail-skeleton__line product-detail-skeleton__line--title" />
            <div className="home-skeleton product-detail-skeleton__line product-detail-skeleton__line--md" />
            <div className="home-skeleton product-detail-skeleton__line product-detail-skeleton__line--price" />
            <div className="home-skeleton product-detail-skeleton__line product-detail-skeleton__line--sm" />
            <div className="home-skeleton product-detail-skeleton__line product-detail-skeleton__line--sm" />
            <div className="home-skeleton product-detail-skeleton__btn" />
          </div>
        </div>
      </div>
    </div>
  );
}

export function CartPageSkeleton() {
  return (
    <div
      className="cart-container cart-page-skeleton"
      role="status"
      aria-live="polite"
      aria-label="Loading cart"
    >
      <div className="cart-items-section">
        <div className="home-skeleton cart-skeleton-title" />
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="cart-skeleton-row">
            <div className="home-skeleton cart-skeleton-img" />
            <div className="cart-skeleton-lines">
              <div className="home-skeleton cart-skeleton-line cart-skeleton-line--lg" />
              <div className="home-skeleton cart-skeleton-line cart-skeleton-line--sm" />
            </div>
            <div className="home-skeleton cart-skeleton-price" />
          </div>
        ))}
      </div>
      <div className="cart-sidebar">
        <div className="home-skeleton cart-skeleton-panel" />
        <div className="home-skeleton cart-skeleton-panel cart-skeleton-panel--tall" />
      </div>
    </div>
  );
}

export function OrdersListSkeleton() {
  return (
    <div
      className="orders-skeleton"
      role="status"
      aria-live="polite"
      aria-label="Loading orders"
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="orders-skeleton-row">
          <div className="home-skeleton orders-skeleton-cell orders-skeleton-cell--wide" />
          <div className="home-skeleton orders-skeleton-cell" />
          <div className="home-skeleton orders-skeleton-cell" />
          <div className="home-skeleton orders-skeleton-cell orders-skeleton-cell--tag" />
        </div>
      ))}
    </div>
  );
}

export function OrderDetailPageSkeleton() {
  return (
    <div
      className="order-details-container order-details-skeleton"
      role="status"
      aria-live="polite"
      aria-label="Loading order"
    >
      <div className="home-skeleton order-details-skeleton__back" />
      <div className="home-skeleton order-details-skeleton__head" />
      <div className="home-skeleton order-details-skeleton__block" />
      <div className="home-skeleton order-details-skeleton__block order-details-skeleton__block--short" />
      <div className="order-details-skeleton__items">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="order-details-skeleton__item-row">
            <div className="home-skeleton order-details-skeleton__thumb" />
            <div className="order-details-skeleton__item-meta">
              <div className="home-skeleton order-details-skeleton__line" />
              <div className="home-skeleton order-details-skeleton__line order-details-skeleton__line--sm" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function BrandsPageSkeleton() {
  return (
    <div
      className="brands-page brands-page-skeleton"
      role="status"
      aria-live="polite"
      aria-label="Loading brands"
    >
      <div className="brands-container">
        <div className="home-skeleton brands-skeleton__title" />
        <div className="brands-skeleton__alphabet">
          {Array.from({ length: 13 }).map((_, i) => (
            <div key={i} className="home-skeleton brands-skeleton__letter" />
          ))}
        </div>
        <div className="brands-skeleton__section">
          <div className="home-skeleton brands-skeleton__section-title" />
          <div className="brands-skeleton__grid">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="brands-skeleton__brand">
                <div className="home-skeleton brands-skeleton__brand-logo" />
                <div className="home-skeleton brands-skeleton__brand-label" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function RoutineCardSkeleton() {
  return (
    <div className="routine-card routine-card-skeleton">
      <div className="home-skeleton routine-card-skeleton__image" />
      <div className="routine-card-skeleton__body">
        <div className="routine-card-skeleton__tags">
          <div className="home-skeleton routine-card-skeleton__tag" />
          <div className="home-skeleton routine-card-skeleton__tag" />
        </div>
        <div className="home-skeleton routine-card-skeleton__title" />
        <div className="home-skeleton routine-card-skeleton__desc" />
        <div className="routine-card-skeleton__meta">
          <div className="home-skeleton routine-card-skeleton__meta-bit" />
          <div className="home-skeleton routine-card-skeleton__meta-bit" />
        </div>
      </div>
    </div>
  );
}

export function RoutinesGridSkeleton({ count = 6 }) {
  return (
    <div
      className="routines-grid routines-grid-skeleton-wrap"
      role="status"
      aria-live="polite"
      aria-label="Loading routines"
    >
      {Array.from({ length: count }).map((_, i) => (
        <RoutineCardSkeleton key={i} />
      ))}
    </div>
  );
}

export function RoutineDetailSkeleton() {
  return (
    <div
      className="routine-detail-page routine-detail-skeleton"
      role="status"
      aria-live="polite"
      aria-label="Loading routine"
    >
      <div className="home-skeleton routine-detail-skeleton__hero" />
      <div className="routine-detail-skeleton__content">
        <div className="home-skeleton routine-detail-skeleton__line routine-detail-skeleton__line--title" />
        <div className="home-skeleton routine-detail-skeleton__line" />
        <div className="home-skeleton routine-detail-skeleton__line routine-detail-skeleton__line--short" />
        <div className="routine-detail-skeleton__products">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="routine-detail-skeleton__product">
              <div className="home-skeleton routine-detail-skeleton__pimg" />
              <div className="home-skeleton routine-detail-skeleton__pline" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function WishlistSkeleton() {
  return (
    <div
      className="myaccount-wish-list wishlist-skeleton"
      role="status"
      aria-live="polite"
      aria-label="Loading wishlist"
    >
      <div className="home-skeleton wishlist-skeleton__title" />
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="wishlist-skeleton__row">
          <div className="home-skeleton wishlist-skeleton__img" />
          <div className="wishlist-skeleton__text">
            <div className="home-skeleton wishlist-skeleton__line wishlist-skeleton__line--lg" />
            <div className="home-skeleton wishlist-skeleton__line wishlist-skeleton__line--sm" />
          </div>
          <div className="home-skeleton wishlist-skeleton__price" />
          <div className="wishlist-skeleton__actions">
            <div className="home-skeleton wishlist-skeleton__btn" />
            <div className="home-skeleton wishlist-skeleton__btn wishlist-skeleton__btn--ghost" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ShippingFeesSkeleton() {
  return (
    <div
      className="shipping-fees-skeleton"
      role="status"
      aria-live="polite"
      aria-label="Loading delivery fees"
    >
      <div className="home-skeleton shipping-fees-skeleton__line" />
      <div className="home-skeleton shipping-fees-skeleton__line shipping-fees-skeleton__line--short" />
    </div>
  );
}

export function ReviewPageSkeleton() {
  return (
    <div
      className="reviewPage review-page-skeleton"
      role="status"
      aria-live="polite"
      aria-label="Loading reviews"
    >
      <div className="reviewPage__content">
        <div className="reviewPage__left">
          <div className="home-skeleton review-skeleton__score" />
          <div className="home-skeleton review-skeleton__sub" />
          <div className="home-skeleton review-skeleton__btn" />
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="home-skeleton review-skeleton__bar" />
          ))}
        </div>
        <div className="reviewPage__right">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="review-skeleton__card">
              <div className="home-skeleton review-skeleton__card-line" />
              <div className="home-skeleton review-skeleton__card-line review-skeleton__card-line--short" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ReferralDashboardSkeleton() {
  return (
    <div
      className="referral-dashboard-skeleton"
      role="status"
      aria-live="polite"
      aria-label="Loading referral data"
    >
      <div className="referral-dashboard-skeleton__cards">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="home-skeleton referral-dashboard-skeleton__card" />
        ))}
      </div>
      <div className="home-skeleton referral-dashboard-skeleton__wide" />
      <div className="home-skeleton referral-dashboard-skeleton__wide referral-dashboard-skeleton__wide--short" />
    </div>
  );
}
